import { useEffect, useState, type FormEvent } from "react";
import type { CreateTripInput, TravelPace, Trip } from "../types";
import { Dialog } from "./Dialog";

interface TripDialogProps {
  open: boolean;
  trip?: Trip | null;
  defaultPace: TravelPace;
  onClose: () => void;
  onSave: (input: CreateTripInput) => void;
}

export function TripDialog({ open, trip, defaultPace, onClose, onSave }: TripDialogProps) {
  const [form, setForm] = useState<CreateTripInput>(() => ({
    name: "",
    destination: "",
    durationDays: 1,
    pace: defaultPace,
    notes: "",
  }));
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setForm(trip ? {
      name: trip.name,
      destination: trip.destination,
      startDate: trip.startDate,
      endDate: trip.endDate,
      durationDays: trip.durationDays,
      pace: trip.pace,
      notes: trip.notes,
    } : { name: "", destination: "", durationDays: 1, pace: defaultPace, notes: "" });
  }, [defaultPace, open, trip]);

  function submit(event: FormEvent) {
    event.preventDefault();
    try {
      onSave(form);
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not save this trip.");
    }
  }

  return (
    <Dialog open={open} title={trip ? "Edit trip" : "Create a trip"} description="Give the route a useful frame. You can build the details as you go." onClose={onClose}>
      <form className="dialog-form" onSubmit={submit}>
        <fieldset className="dialog-section"><legend>Trip details</legend><div className="field-grid">
          <label className="field field-span"><span>Trip name</span><input autoFocus required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Autumn in Kyoto" /></label>
          <label className="field field-span"><span>Destination</span><input required value={form.destination} onChange={(event) => setForm({ ...form, destination: event.target.value })} placeholder="Kyoto, Japan" /></label>
          <label className="field field-span"><span>Planning note <small>optional</small></span><textarea rows={3} value={form.notes ?? ""} onChange={(event) => setForm({ ...form, notes: event.target.value })} placeholder="What should shape this trip?" /></label>
        </div></fieldset>
        <fieldset className="dialog-section"><legend>Schedule</legend><div className="field-grid">
          <label className="field"><span>Starts <small>optional</small></span><input type="date" value={form.startDate ?? ""} onChange={(event) => setForm({ ...form, startDate: event.target.value || undefined })} /></label>
          <label className="field"><span>Ends <small>optional</small></span><input type="date" min={form.startDate} value={form.endDate ?? ""} onChange={(event) => setForm({ ...form, endDate: event.target.value || undefined })} /></label>
          <label className="field"><span>Duration <small>required when dates are blank</small></span><input type="number" min="1" max="30" step="1" value={form.durationDays ?? ""} onChange={(event) => setForm({ ...form, durationDays: Number(event.target.value) })} /></label>
          <label className="field"><span>Pace</span><select value={form.pace ?? defaultPace} onChange={(event) => setForm({ ...form, pace: event.target.value as TravelPace })}><option value="relaxed">Relaxed</option><option value="balanced">Balanced</option><option value="packed">Packed</option></select></label>
        </div></fieldset>
        <p className="form-error" role="alert">{error}</p>
        <div className="dialog-actions"><button type="button" className="button button-secondary" onClick={onClose}>Cancel</button><button className="button button-primary" type="submit">{trip ? "Save changes" : "Create trip"}</button></div>
      </form>
    </Dialog>
  );
}
